import {
  Box,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  TextField,
  Switch,
} from '@mui/material';
import { FC } from 'react';
import { commonStyles } from '../../../../utils/styles/inputs';
import theme from '../../../../theme';

export interface WhatsappConfigValues {
  model: string;
  historyResponse: number | undefined;
  debugMode: boolean;
  whatsappPhonenumberId: string;
  whatsappToken: string;
  whatsappVerificationToken: string;
}

interface ModelOption {
  value: string;
  label: string;
}

interface AgentWhatsappConfigProps {
  isDrawer?: boolean;
  whatsappConfig: WhatsappConfigValues;
  onModelChange: (value: string) => void;
  onHistoryResponsesChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onDebugModeChange: (value: boolean) => void;
  onWhatsappPhoneNumberIdChange: (value: string) => void;
  onWhatsappTokenChange: (value: string) => void;
  onWhatsappVerifyTokenChange: (value: string) => void;
  modelOptions?: ModelOption[];
}

const AgentWhatsappConfig: FC<AgentWhatsappConfigProps> = ({
  isDrawer,
  whatsappConfig,
  onModelChange,
  onHistoryResponsesChange,
  onDebugModeChange,
  onWhatsappPhoneNumberIdChange,
  onWhatsappTokenChange,
  onWhatsappVerifyTokenChange,
  modelOptions,
}) => {
  const commonSelectStyles = {
    '& .MuiSelect-select': {
      fontSize: '14px',
      fontWeight: 400,
      background: '#F8F9FC',
      display: 'flex',
      padding: '8px 12px',
      alignItems: 'center',
      borderRadius: '6px',
      border: '1px solid #DCE0E7',
      lineHeight: 'normal',
      letterSpacing: '-0.14px',
      color: theme.palette.lightGray.text,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  const handleDebugModeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onDebugModeChange(event.target.checked);
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#fff', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mt: 1,
          px: isDrawer ? 2 : 0,
        }}
      >
        {/* Model Selection */}
        <FormControl fullWidth size="small">
          <FormLabel sx={commonStyles.formLabel}>Model</FormLabel>
          <Select
            value={whatsappConfig.model}
            sx={commonSelectStyles}
            inputProps={{ 'aria-label': 'Model selection' }}
            onChange={e => onModelChange(e.target.value)}
          >
            {Array.isArray(modelOptions)
              ? modelOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>

        {/* Number of History Responses */}
        <FormControl fullWidth size="small">
          <FormLabel sx={commonStyles.formLabel}>
            No. of History Responses
          </FormLabel>
          <TextField
            type="number"
            value={whatsappConfig.historyResponse || ''}
            onChange={onHistoryResponsesChange}
            variant="outlined"
            fullWidth
            inputProps={{
              min: 0,
              max: 50,
            }}
            sx={{
              ...commonStyles.textField,
              '& .MuiInputBase-input': {
                fontSize: '14px',
                fontWeight: 400,
                padding: '8px 12px',
              },
            }}
          />
        </FormControl>

        {/* WhatsApp Phone Number ID */}
        <FormControl fullWidth size="small">
          <FormLabel sx={commonStyles.formLabel}>
            WhatsApp Phone Number ID
          </FormLabel>
          <TextField
            value={whatsappConfig.whatsappPhonenumberId}
            onChange={e => onWhatsappPhoneNumberIdChange(e.target.value)}
            placeholder="Enter WhatsApp Phone Number ID"
            variant="outlined"
            fullWidth
            sx={{
              ...commonStyles.textField,
              '& .MuiInputBase-input': {
                fontSize: '14px',
                fontWeight: 400,
                padding: '8px 12px',
              },
            }}
          />
        </FormControl>

        {/* WhatsApp Token */}
        <FormControl fullWidth size="small">
          <FormLabel sx={commonStyles.formLabel}>WhatsApp Token</FormLabel>
          <TextField
            value={whatsappConfig.whatsappToken}
            onChange={e => onWhatsappTokenChange(e.target.value)}
            placeholder="Enter WhatsApp Token"
            variant="outlined"
            fullWidth
            sx={{
              ...commonStyles.textField,
              '& .MuiInputBase-input': {
                fontSize: '14px',
                fontWeight: 400,
                padding: '8px 12px',
              },
            }}
          />
        </FormControl>

        {/* WhatsApp Verify Token */}
        <FormControl fullWidth size="small">
          <FormLabel sx={commonStyles.formLabel}>
            WhatsApp Verify Token
          </FormLabel>
          <TextField
            value={whatsappConfig.whatsappVerificationToken}
            onChange={e => onWhatsappVerifyTokenChange(e.target.value)}
            placeholder="Enter WhatsApp Verify Token"
            variant="outlined"
            fullWidth
            sx={commonStyles.textField}
          />
        </FormControl>

        {/* Debug Mode Toggle */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <FormLabel sx={commonStyles.formLabel}>Debug Mode</FormLabel>
          <Switch
            checked={whatsappConfig.debugMode}
            onChange={handleDebugModeChange}
            color="primary"
            size="small"
            sx={{
              width: 36,
              height: 20,
              padding: 0,
              '& .MuiSwitch-switchBase': {
                padding: 0,
                margin: '2px',
                transitionDuration: '300ms',
                '&.Mui-checked': {
                  transform: 'translateX(16px)',
                  color: '#fff',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#6366F1',
                    opacity: 1,
                    border: 0,
                  },
                },
                '&.Mui-focusVisible .MuiSwitch-thumb': {
                  color: '#6366F1',
                  border: '6px solid #fff',
                },
              },
              '& .MuiSwitch-thumb': {
                boxSizing: 'border-box',
                width: 16,
                height: 16,
              },
              '& .MuiSwitch-track': {
                borderRadius: 26 / 2,
                backgroundColor: '#E9E9EA',
                opacity: 1,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AgentWhatsappConfig;
